import { dispatchFabricEvent, addFabricEventListener } from '../../../hooks/useFabricEvent.js';
import { isString } from '@fabric-app/utils/guards';
import { Extension } from '@tiptap/core';
import { DOMParser, Node, Fragment } from '@tiptap/pm/model';
import { PluginKey, Plugin } from '@tiptap/pm/state';
import { DecorationSet, Decoration } from '@tiptap/pm/view';
import * as jsdiff from 'diff';
import { generateHTMLFromFragment } from '../../content.js';
import { getSimilarity, simpleHashString, obfuscateText } from '@fabric-app/utils/text';
import { renderDiffDisplay } from './DiffDisplay.js';
import { createLogger } from '@fabric-app/utils/logging';
import { getFbcDocumentResourceId } from '../FbcDocument.js';

const decodeHtmlEntities = (str) => {
  const txt = document.createElement("textarea");
  txt.innerHTML = str;
  return txt.value;
};
const findNodeInFragmentTextPos = (fragment, textPos) => {
  let found = false;
  let foundNode = null;
  fragment.forEach((node, pos) => {
    if (found) return;
    if (pos + node.textContent.length >= textPos) {
      found = true;
      foundNode = node;
      return;
    }
  });
  return foundNode;
};
function calculateChangeDetails(searchContent, replaceContent) {
  const searchText = searchContent.textBetween(0, searchContent.size) ?? "";
  const replaceText = replaceContent.textBetween(0, replaceContent.size) ?? "";
  if (replaceText.trim() === "") {
    return {
      additions: [],
      deletions: [
        {
          from: 0,
          to: searchText.length,
          value: searchText
        }
      ],
      type: "deletions_only",
      replaceToSearchCharMap: Array(searchText.length).fill(-1)
    };
  }
  const diff = jsdiff.diffWordsWithSpace(searchText, replaceText, {
    intlSegmenter: new Intl.Segmenter("en", {
      granularity: "word"
    })
  });
  const rawAdditions = [];
  const rawDeletions = [];
  let searchPos = 0;
  let replacePos = 0;
  const replaceToSearchCharMap = new Array(replaceText.length);
  let mapSearchIdx = 0;
  let mapReplaceIdx = 0;
  for (let i = 0; i < diff.length; i++) {
    const part = diff[i];
    if (part.added) {
      rawAdditions.push({
        from: replacePos,
        to: replacePos + part.value.length,
        value: part.value
      });
      for (let k = 0; k < part.value.length; k++) {
        replaceToSearchCharMap[mapReplaceIdx + k] = -1;
      }
      replacePos += part.value.length;
      mapReplaceIdx += part.value.length;
    } else if (part.removed) {
      rawDeletions.push({
        from: searchPos,
        to: searchPos + part.value.length,
        value: part.value
      });
      searchPos += part.value.length;
      mapSearchIdx += part.value.length;
    } else {
      for (let k = 0; k < part.value.length; k++) {
        replaceToSearchCharMap[mapReplaceIdx + k] = mapSearchIdx + k;
      }
      searchPos += part.value.length;
      replacePos += part.value.length;
      mapSearchIdx += part.value.length;
      mapReplaceIdx += part.value.length;
    }
  }
  const additions = joinAdjacentSegments(rawAdditions, replaceText, false).filter((add) => add.value.length > 0);
  const deletions = joinAdjacentSegments(rawDeletions, searchText, false).filter((del) => del.value.length > 0);
  let type = "very_different";
  if (additions.length > 0 && deletions.length === 0) {
    type = "additions_only";
  } else if (additions.length === 0 && deletions.length > 0) {
    type = "deletions_only";
  } else if (additions.length > 0 && deletions.length > 0) {
    type = "mixed";
  }
  const totalSearchLength = searchText.length;
  const totalReplaceLength = replaceText.length;
  const addedCharsTotal = additions.reduce((sum, add) => sum + add.value.length, 0);
  const deletedCharsTotal = deletions.reduce((sum, del) => sum + del.value.length, 0);
  const changeRatio = (addedCharsTotal + deletedCharsTotal) / (totalSearchLength + totalReplaceLength);
  if (changeRatio > 0.8) {
    type = "very_different";
  }
  const modifiedChars = [];
  for (let i = 0; i < replaceText.length; i++) {
    const oldIndex = replaceToSearchCharMap[i];
    if (oldIndex === -1) {
      continue;
    } else {
      const originalNode = findNodeInFragmentTextPos(searchContent, oldIndex);
      const newNode = findNodeInFragmentTextPos(replaceContent, i);
      if (!originalNode || !newNode) {
        continue;
      }
      const isDifferent = originalNode.type.name !== newNode.type.name || Object.entries(originalNode.attrs).some(([key, value]) => newNode.attrs[key] !== value) || originalNode.marks.some((mark) => !newNode.marks.some((m) => m.type.name === mark.type.name && Object.entries(m.attrs).every(([key, value]) => m.attrs[key] === value)));
      if (isDifferent) {
        modifiedChars.push(i);
      }
    }
  }
  if (modifiedChars.length > 0) {
    let segmentStartIdx = modifiedChars[0] - 1;
    for (let k = 1; k <= modifiedChars.length; k++) {
      if (k === modifiedChars.length || modifiedChars[k] !== modifiedChars[k - 1] + 1) {
        const segmentEndIdx = modifiedChars[k - 1];
        const value = replaceText.substring(segmentStartIdx, segmentEndIdx);
        additions.push({
          from: segmentStartIdx,
          to: segmentStartIdx + value.length,
          value
        });
        if (k < modifiedChars.length) {
          segmentStartIdx = modifiedChars[k] - 1;
        }
      }
    }
  }
  const originalIndicesForDeletion = modifiedChars.map((modIdx) => replaceToSearchCharMap[modIdx]).filter((idx) => idx !== -1);
  if (originalIndicesForDeletion.length > 0) {
    const sortedUniqueOriginalIndices = Array.from(new Set(originalIndicesForDeletion)).sort((a, b) => a - b);
    if (sortedUniqueOriginalIndices.length > 0) {
      let segmentStartIdx = sortedUniqueOriginalIndices[0] - 1;
      for (let k = 1; k <= sortedUniqueOriginalIndices.length; k++) {
        if (k === sortedUniqueOriginalIndices.length || sortedUniqueOriginalIndices[k] !== sortedUniqueOriginalIndices[k - 1] + 1) {
          const segmentEndIdx = sortedUniqueOriginalIndices[k - 1];
          const value = searchText.substring(segmentStartIdx, segmentEndIdx);
          deletions.push({
            from: segmentStartIdx,
            to: segmentStartIdx + value.length,
            value
          });
          if (k < sortedUniqueOriginalIndices.length) {
            segmentStartIdx = sortedUniqueOriginalIndices[k] - 1;
          }
        }
      }
    }
  }
  return {
    additions,
    deletions,
    type,
    replaceToSearchCharMap
  };
}
function joinAdjacentSegments(segments, originalText, trim = true) {
  if (segments.length <= 1) {
    if (segments.length === 1 && trim) {
      const segment = segments[0];
      const value = segment.value;
      const leadingMatch = value.match(/^(\s+)/);
      const leadingCount = leadingMatch ? leadingMatch[0].length : 0;
      const trailingMatch = value.match(/(\s+)$/);
      const trailingCount = trailingMatch ? trailingMatch[0].length : 0;
      if (leadingCount > 0 || trailingCount > 0) {
        const trimmedValue = value.trim();
        return [
          {
            from: segment.from + leadingCount,
            to: segment.to - trailingCount,
            value: trimmedValue
          }
        ];
      }
    }
    return segments;
  }
  const sortedSegments = [
    ...segments
  ].sort((a, b) => a.from - b.from);
  const result = [];
  let current = sortedSegments[0];
  for (let i = 1; i < sortedSegments.length; i++) {
    const next = sortedSegments[i];
    const textBetween = originalText.substring(current.to, next.from);
    const isOnlyWhitespace = /^\s*$/.test(textBetween);
    if (isOnlyWhitespace) {
      current = {
        from: current.from,
        to: next.to,
        value: current.value + textBetween + next.value
      };
    } else {
      if (trim) {
        const value = current.value;
        const leadingMatch = value.match(/^(\s+)/);
        const leadingCount = leadingMatch ? leadingMatch[0].length : 0;
        const trailingMatch = value.match(/(\s+)$/);
        const trailingCount = trailingMatch ? trailingMatch[0].length : 0;
        if (leadingCount > 0 || trailingCount > 0) {
          const trimmedValue = value.trim();
          result.push({
            from: current.from + leadingCount,
            to: current.to - trailingCount,
            value: trimmedValue
          });
        } else {
          result.push(current);
        }
      } else {
        result.push(current);
      }
      current = next;
    }
  }
  if (trim) {
    const value = current.value;
    const leadingMatch = value.match(/^(\s+)/);
    const leadingCount = leadingMatch ? leadingMatch[0].length : 0;
    const trailingMatch = value.match(/(\s+)$/);
    const trailingCount = trailingMatch ? trailingMatch[0].length : 0;
    if (leadingCount > 0 || trailingCount > 0) {
      const trimmedValue = value.trim();
      result.push({
        from: current.from + leadingCount,
        to: current.to - trailingCount,
        value: trimmedValue
      });
    } else {
      result.push(current);
    }
  } else {
    result.push(current);
  }
  return result;
}
function computeLocator(pos, state) {
  const $pos = state.doc.resolve(pos);
  const ancestors = [];
  let foundTrusted = false;
  let depth = $pos.depth;
  let index = $pos.index();
  let currentNode = $pos.parent.maybeChild(index);
  while (depth > -1 && !foundTrusted) {
    if (!currentNode) break;
    const attrs = currentNode.attrs;
    const dataUuid = attrs["data-uuid"];
    if (dataUuid) {
      foundTrusted = true;
    }
    ancestors.push(currentNode);
    currentNode = $pos.node(depth);
    index = $pos.index(depth);
    depth--;
  }
  const locator = ancestors.reduce((acc, node) => {
    const dataUuid = node.attrs["data-uuid"];
    return {
      type: node.type.name,
      attrs: dataUuid && isString(dataUuid) ? {
        ["data-uuid"]: dataUuid
      } : {},
      content: acc !== null ? acc : void 0,
      textContent: node.textContent
    };
  }, null);
  return locator;
}
function findNodeFromEditorLocator(doc, locator) {
  let result = null;
  doc.descendants((node, pos, _parent, _index) => {
    if (result) return false;
    if (node.type.name === locator.type && node.attrs["data-uuid"] === locator.attrs["data-uuid"]) {
      const content = locator.content;
      if (content) {
        let childPos = pos + 1;
        let foundNestedContent = false;
        node.forEach((childNode) => {
          if (foundNestedContent) return;
          if (childNode.type.name === content.type) {
            const hasMatchingUuid = content.attrs["data-uuid"] && childNode.attrs["data-uuid"] === content.attrs["data-uuid"];
            let hasMatchingText = false;
            if (content.textContent && childNode.textContent) {
              const similarity = getSimilarity(childNode.textContent, content.textContent);
              hasMatchingText = similarity >= 0.9;
            }
            if (hasMatchingUuid || hasMatchingText) {
              result = {
                pos: childPos,
                node: childNode
              };
              foundNestedContent = true;
              return;
            }
          }
          childPos += childNode.nodeSize;
        });
        if (!foundNestedContent) return true;
        return false;
      } else {
        if (locator.textContent && node.textContent) {
          const similarity = getSimilarity(node.textContent, locator.textContent);
          if (similarity < 0.9) {
            return true;
          }
        }
        result = {
          pos,
          node
        };
        return false;
      }
    }
    return true;
  });
  return result;
}
function findTextPosition(node, textOffset) {
  let textIndex = 0;
  let textPos = 0;
  let found = false;
  node.descendants((child, pos) => {
    if (!child.isText || found) {
      return true;
    }
    const text = child.textContent;
    if (!text) {
      return true;
    }
    textPos = pos;
    if (textIndex + text.length >= textOffset) {
      textIndex = textOffset - textIndex;
      found = true;
      return false;
    }
    textIndex += text.length;
  });
  return textPos + textIndex;
}
const createAdditionDecoration = (content) => {
  const span = document.createElement("span");
  span.dataset.diffPreviewAddition = "true";
  span.textContent = content;
  return span;
};
const DiffPluginKey = new PluginKey("diff");
const applyDiffToTransaction = (editorView, diff, tr = editorView.state.tr, replaceWith) => {
  const result = findNodeFromEditorLocator(tr.doc, diff.locator);
  if (!result) {
    return tr;
  }
  const { pos, node } = result;
  if (diff.replaceContent.size === 0) {
    tr.delete(pos, pos + node.nodeSize);
  } else {
    tr.replaceWith(pos, pos + node.nodeSize, replaceWith || (diff.isSelf ? diff.replaceContent : node.copy(diff.replaceContent)));
  }
  return tr;
};
const getNodeDataForLogging = (doc, posOffset = 0) => {
  const nodeData = [];
  doc.forEach((node, pos, index) => {
    const data = {
      type: node.type.name,
      attrs: node.attrs,
      pos: pos + posOffset,
      index,
      childCount: node.childCount,
      ...node.inlineContent || node.isInline ? {
        textContent: obfuscateText(node.textContent)
      } : {}
    };
    if (node.childCount > 0) {
      data.children = getNodeDataForLogging(node, pos + 1);
    }
    nodeData.push(data);
  });
  return nodeData;
};
class DiffState {
  constructor(diffs, decorations) {
    this.diffs = [];
    this.decorations = DecorationSet.empty;
    this.diffs = diffs;
    this.decorations = decorations;
    this.logger = createLogger("diffState");
  }
  getTransactionWithNewDiffs(chatbotDiffs, editorView, messageId, transaction = editorView.state.tr, override) {
    const resourceId = getFbcDocumentResourceId(editorView.state);
    if (!resourceId) {
      this.logger.warn("DiffHandler: No resourceId found", {
        messageId,
        diffs: chatbotDiffs
      });
      return transaction;
    }
    this.logger.log("chatbotDiffs", chatbotDiffs);
    const editorDiffs = [];
    const dom = editorView.dom;
    const parser = DOMParser.fromSchema(editorView.state.schema);
    const existingTargetNodes = override ? [] : this.diffs.flatMap((diff) => {
      const result = findNodeFromEditorLocator(editorView.state.doc, diff.locator);
      if (!result) return [];
      return [
        result.node
      ];
    });
    for (const diff of chatbotDiffs) {
      if (!diff.location) continue;
      const isRegex = diff.search.regex;
      const isRoot = diff.location === "root";
      const elements = isRoot ? getAllTailBlockElements(editorView) : Array.from(dom.querySelectorAll(diff.location));
      this.logger.log("elements for diff index", diff.index, elements);
      if (elements.length === 0) continue;
      if (isRegex) {
        continue;
      } else {
        const bestMatchDiff = processNonRegexDiff(diff, messageId, elements, editorView, parser, existingTargetNodes);
        if (bestMatchDiff) {
          editorDiffs.push(bestMatchDiff);
          const result = findNodeFromEditorLocator(editorView.state.doc, bestMatchDiff.locator);
          if (result) {
            existingTargetNodes.push(result.node);
          }
        }
      }
    }
    this.logger.log("editorDiffs", editorDiffs);
    if (editorDiffs.length > 0) {
      transaction.setMeta(DiffPluginKey, {
        override,
        diffs: editorDiffs
      });
      editorView.dispatch(transaction);
      dispatchFabricEvent("fabric-ai-diff-status", {
        messageId,
        resourceId,
        source: "notepad",
        pending: true,
        failed: false
      });
      if (editorDiffs.length < chatbotDiffs.length) {
        this.logger.warn("DiffHandler found less diffs than expected", {
          messageId,
          expected: chatbotDiffs.length,
          diffs: chatbotDiffs,
          resultDiffs: editorDiffs,
          found: editorDiffs.length,
          nodeData: getNodeDataForLogging(editorView.state.doc)
        });
      }
    } else {
      dispatchFabricEvent("fabric-ai-diff-status", {
        messageId,
        resourceId,
        source: "notepad",
        pending: false,
        failed: true
      });
      this.logger.error("Failed to apply diffs", {
        messageId,
        override,
        diffs: editorDiffs,
        resultDiffs: editorDiffs,
        nodeData: getNodeDataForLogging(editorView.state.doc)
      });
    }
    return transaction;
  }
  getTransactionWithRemovedDiffs(editorView, messageId, apply = false, transaction = editorView.state.tr) {
    const targetDiffs = this.diffs.filter((diff) => diff.context.messageId === messageId);
    transaction.setMeta(DiffPluginKey, {
      remove: targetDiffs.map((diff) => diff.hash)
    });
    if (apply) {
      for (let i = targetDiffs.length - 1; i >= 0; i--) {
        const diff = targetDiffs[i];
        applyDiffToTransaction(editorView, diff, transaction);
      }
    }
    return transaction;
  }
  getIsMessageIdPending(messageId) {
    return this.diffs.some((diff) => diff.context.messageId === messageId);
  }
  applyNewDiffs(editorDiffs, override = false) {
    const newDiffs = override ? editorDiffs : [
      ...this.diffs,
      ...editorDiffs
    ];
    return new DiffState(newDiffs, this.decorations);
  }
  applyRemoveDiffs(hashes, state) {
    const resourceId = getFbcDocumentResourceId(state);
    if (!resourceId) {
      this.logger.warn("DiffHandler: No resourceId found", {
        hashes
      });
      return this;
    }
    const { messagesCompletion, remainingDiffs } = this.diffs.reduce((acc, diff) => {
      if (hashes.includes(diff.hash)) {
        if (acc.messagesCompletion[diff.context.messageId] !== false) acc.messagesCompletion[diff.context.messageId] = true;
      } else {
        acc.remainingDiffs.push(diff);
        acc.messagesCompletion[diff.context.messageId] = false;
      }
      return acc;
    }, {
      messagesCompletion: {},
      remainingDiffs: []
    });
    for (const [messageId, isComplete] of Object.entries(messagesCompletion)) {
      if (!isComplete) continue;
      dispatchFabricEvent("fabric-ai-diff-apply", {
        messageId,
        action: "complete",
        source: "notepad",
        resourceId
      });
    }
    return new DiffState(remainingDiffs, this.decorations);
  }
  applyDecorations(state) {
    const decorationList = [];
    for (const diff of this.diffs) {
      const result = findNodeFromEditorLocator(state.doc, diff.locator);
      if (!result) {
        continue;
      }
      const { pos, node } = result;
      const inlineDecorations = [];
      if (diff.changeDetails.type === "very_different") {
        inlineDecorations.push(Decoration.inline(pos, pos + node.nodeSize - 1, {
          ["data-diff-preview-deletion"]: "true"
        }));
      } else if (diff.changeDetails.type === "mixed" || diff.changeDetails.type === "deletions_only") {
        for (const deletion of diff.changeDetails.deletions) {
          const mappedFrom = pos + 1 + findTextPosition(node, deletion.from);
          const mappedTo = pos + 1 + findTextPosition(node, deletion.to);
          inlineDecorations.push(Decoration.inline(mappedFrom, mappedTo, {
            ["data-diff-preview-deletion"]: "true"
          }));
        }
      } else if (diff.changeDetails.type === "additions_only") {
        let offset = 0;
        for (const addition of diff.changeDetails.additions) {
          const mappedFrom = pos + 1 + findTextPosition(node, addition.from - offset);
          offset += addition.value.length;
          inlineDecorations.push(Decoration.widget(mappedFrom, createAdditionDecoration(addition.value), {
            key: `diff-${diff.hash}-${mappedFrom}`
          }));
        }
      }
      const nodeDecoration = Decoration.node(pos, pos + node.nodeSize, {
        ["data-diff-preview-from"]: "true"
      });
      const zIndex = Math.floor(this.diffs.length * 10 * (state.doc.nodeSize - pos) / state.doc.nodeSize);
      const previewDecoration = Decoration.widget(pos + node.nodeSize, renderDiffDisplay(diff, zIndex), {
        destroy: (node2) => {
          if (node2 instanceof HTMLElement && node2.reactRoot) {
            node2.reactRoot.unmount();
          }
        },
        key: diff.hash
      });
      decorationList.push(nodeDecoration, previewDecoration, ...inlineDecorations);
    }
    return new DiffState(this.diffs, decorationList.length > 0 ? DecorationSet.create(state.doc, decorationList) : DecorationSet.empty);
  }
}
const setupView = (editorView) => {
  const unsubscribePropose = addFabricEventListener("fabric-ai-diff-propose", (e) => {
    const diffState = DiffPluginKey.getState(editorView.state);
    const resourceId2 = getFbcDocumentResourceId(editorView.state);
    if (!diffState || resourceId2 !== e.detail.resourceId) return;
    const transaction = diffState.getTransactionWithNewDiffs(e.detail.diffs, editorView, e.detail.messageId, void 0, e.detail.override);
    editorView.dispatch(transaction);
  });
  const unsubscribeApply = addFabricEventListener("fabric-ai-diff-apply", (e) => {
    if (e.detail.source === "notepad") return;
    const resourceId2 = getFbcDocumentResourceId(editorView.state);
    const state2 = DiffPluginKey.getState(editorView.state);
    if (!state2 || resourceId2 !== e.detail.resourceId && !(e.detail.action === "reject" && !e.detail.resourceId)) return;
    const tr = state2.getTransactionWithRemovedDiffs(editorView, e.detail.messageId, e.detail.action === "apply");
    editorView.dispatch(tr);
  });
  const unsubscribeStatus = addFabricEventListener("fabric-ai-diff-status", (e) => {
    if (e.detail.source === "notepad") return;
    const messageId = e.detail.messageId;
    const state2 = DiffPluginKey.getState(editorView.state);
    const resourceId2 = getFbcDocumentResourceId(editorView.state);
    if (!state2 || !messageId || resourceId2 !== e.detail.resourceId) return;
    const pending = state2.getIsMessageIdPending(messageId);
    dispatchFabricEvent("fabric-ai-diff-status", {
      messageId,
      resourceId: resourceId2,
      source: "notepad",
      pending,
      failed: false
    });
  });
  const state = DiffPluginKey.getState(editorView.state);
  const resourceId = getFbcDocumentResourceId(editorView.state);
  if (state && resourceId) {
    const messageIds = /* @__PURE__ */ new Set();
    for (const diff of state.diffs) {
      messageIds.add(diff.context.messageId);
    }
    for (const messageId of messageIds) {
      dispatchFabricEvent("fabric-ai-diff-status", {
        messageId,
        resourceId,
        source: "notepad",
        pending: false,
        failed: false
      });
    }
    if (messageIds.size === 0) {
      dispatchFabricEvent("fabric-ai-diff-status", {
        messageId: null,
        resourceId,
        source: "notepad",
        pending: false,
        failed: false
      });
    }
  }
  return () => {
    unsubscribePropose();
    unsubscribeApply();
    unsubscribeStatus();
  };
};
const DiffHandler = Extension.create({
  name: "diffHandler",
  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: DiffPluginKey,
        view(editorView) {
          if (!editorView.editable) return {
            destroy: () => {
            }
          };
          let unsubscribe = setupView(editorView);
          return {
            update(view, oldState) {
              const oldResourceId = getFbcDocumentResourceId(oldState);
              const newResourceId = getFbcDocumentResourceId(view.state);
              if (oldResourceId !== newResourceId) {
                unsubscribe();
                unsubscribe = setupView(view);
              }
            },
            destroy() {
              unsubscribe();
            }
          };
        },
        state: {
          init() {
            return new DiffState([], DecorationSet.empty);
          },
          apply(transaction, diffState, _oldState, state) {
            const set = transaction.getMeta(DiffPluginKey);
            if (set && set.diffs) diffState = diffState.applyNewDiffs(set.diffs, set.override);
            if (set && set.remove) diffState = diffState.applyRemoveDiffs(set.remove, _oldState);
            if (transaction.docChanged || set && set.remove || set && set.diffs) diffState = diffState.applyDecorations(state);
            return diffState;
          }
        },
        props: {
          decorations(state) {
            return this.getState(state)?.decorations ?? DecorationSet.empty;
          }
        }
      })
    ];
  }
});
const findRealContent = (node, content) => {
  if (!node) {
    return null;
  }
  if (node instanceof Node && (node.type.name === "doc" || node.type.name === "placeholderDoc")) {
    if (node.childCount > 1) {
      return findRealContent(node.content);
    }
    return findRealContent(node.firstChild);
  }
  if (node instanceof Fragment) {
    let result = Fragment.empty;
    for (let i = 0; i < node.childCount; i++) {
      const child = node.child(i);
      const childResult = findRealContent(child);
      if (childResult) {
        result = result.append(Fragment.from(childResult));
      }
    }
    if (result.childCount === 0) {
      return null;
    }
    return result;
  }
  return node;
};
const getAllTailBlockElements = (view) => {
  const elements = [];
  view.state.doc.descendants((node, pos) => {
    if (node instanceof Node && node.isBlock && node.firstChild?.isText && node.lastChild?.isText) {
      const htmlNode = view.nodeDOM(pos);
      if (htmlNode && htmlNode instanceof Element && elements.every((element) => element !== htmlNode && !element.contains(htmlNode))) {
        elements.push(htmlNode);
      }
    }
  });
  return elements;
};
function calculateAttributeSimilaritySimple(patternAttrs, matchAttrs) {
  const patternKeys = Object.keys(patternAttrs);
  if (patternKeys.includes("src")) {
    if (patternAttrs.src !== matchAttrs.src) {
      return 0;
    }
  }
  if (patternKeys.length === 0) {
    return 1;
  }
  let matchedCount = 0;
  for (const key of patternKeys) {
    if (patternAttrs[key] === matchAttrs[key]) {
      matchedCount++;
    }
  }
  return matchedCount / patternKeys.length;
}
const findBestFuzzyMatch = (patternFragment, sourceFragment, similarityThreshold = 0.95, sourceFragmentGlobalOffset = 0) => {
  if (!patternFragment || patternFragment.size === 0 || !sourceFragment) {
    console.warn("findBestFuzzyMatch: Invalid input fragments or empty patternFragment.");
    return null;
  }
  if (typeof similarityThreshold !== "number" || similarityThreshold < 0 || similarityThreshold > 1) {
    console.warn("findBestFuzzyMatch: similarityThreshold must be a number between 0 and 1.");
    return null;
  }
  if (patternFragment.size > sourceFragment.size) {
    return null;
  }
  let bestMatch = null;
  const patternText = patternFragment.textBetween(0, patternFragment.size, " ");
  for (let offsetInSource = 0; offsetInSource <= sourceFragment.size - patternFragment.size; offsetInSource++) {
    const potentialMatchFragment = sourceFragment.cut(offsetInSource, offsetInSource + patternFragment.size);
    const potentialMatchText = potentialMatchFragment.textBetween(0, potentialMatchFragment.size, " ");
    let combinedSimilarity = 0;
    const textSimilarity = getSimilarity(patternText, potentialMatchText);
    let accumulatedAttributeSimilarity = 0;
    let attributeComparisonNodeCount = 0;
    for (let i = 0; i < patternFragment.childCount; i++) {
      const patternNode = patternFragment.child(i);
      if (patternNode.isAtom && !patternNode.isText) {
        attributeComparisonNodeCount++;
        const potentialMatchNode = potentialMatchFragment.childCount > i ? potentialMatchFragment.child(i) : null;
        if (potentialMatchNode && potentialMatchNode.type === patternNode.type) {
          accumulatedAttributeSimilarity += calculateAttributeSimilaritySimple(patternNode.attrs, potentialMatchNode.attrs);
        } else {
          accumulatedAttributeSimilarity += 0;
        }
      }
    }
    let averageAttributeSimilarity = 0;
    if (attributeComparisonNodeCount > 0) {
      averageAttributeSimilarity = accumulatedAttributeSimilarity / attributeComparisonNodeCount;
    }
    if (attributeComparisonNodeCount > 0 && patternText.trim().length > 0) {
      combinedSimilarity = textSimilarity * 0.5 + averageAttributeSimilarity * 0.5;
    } else if (attributeComparisonNodeCount > 0) {
      combinedSimilarity = averageAttributeSimilarity;
    } else {
      combinedSimilarity = textSimilarity;
    }
    combinedSimilarity = Math.max(0, Math.min(1, combinedSimilarity));
    if (combinedSimilarity >= similarityThreshold) {
      const currentMatchData = {
        start: sourceFragmentGlobalOffset + offsetInSource,
        end: sourceFragmentGlobalOffset + offsetInSource + potentialMatchFragment.size,
        similarity: combinedSimilarity
      };
      if (!bestMatch || combinedSimilarity > bestMatch.similarity) {
        bestMatch = currentMatchData;
      }
      if (bestMatch && bestMatch.similarity === 1) {
        return bestMatch;
      }
    }
  }
  return bestMatch;
};
const processNonRegexDiff = (diff, messageId, elements, editorView, parser, existingTargetNodes) => {
  let bestMatch = null;
  for (let i = 0; i < elements.length; i++) {
    const element = elements[i];
    const parent = element.parentNode;
    const children = Array.from(parent?.childNodes ?? []);
    const offset = children.indexOf(element);
    if (!parent || offset < 0) continue;
    const pos = editorView.posAtDOM(parent, offset);
    const node = pos < 0 ? editorView.state.doc : editorView.state.doc.nodeAt(pos);
    if (!node) continue;
    let parentNode = void 0;
    const grandParent = parent.parentNode;
    const grandParentChildren = Array.from(grandParent?.childNodes ?? []);
    if (parent instanceof Element && grandParent) {
      const parentOffset = grandParentChildren.indexOf(parent);
      const parentPos = editorView.posAtDOM(grandParent, parentOffset);
      parentNode = (parentPos < 0 ? editorView.state.doc : editorView.state.doc.nodeAt(parentPos)) ?? void 0;
    }
    if (existingTargetNodes.includes(node)) continue;
    const searchTextBase = diff.search.value;
    const searchElement2 = document.createElement("div");
    searchElement2.innerHTML = searchTextBase;
    const searchContent2 = Fragment.from(findRealContent(parser.parse(searchElement2, {
      topNode: editorView.state.schema.nodes.placeholderDoc.create()
    }), diff.search.value));
    if (!searchContent2 || !(searchContent2 instanceof Fragment)) continue;
    const searchText = searchContent2.toString() ?? diff.search.value;
    const nodeContentText = Fragment.from(node.content).toString();
    const contentSimilarity = getSimilarity(nodeContentText, searchText);
    const selfContentText = Fragment.from(node).toString();
    const selfSimilarity = getSimilarity(selfContentText, searchText);
    const fullSimilarity = Math.max(contentSimilarity, selfSimilarity);
    const isSelf = selfSimilarity > contentSimilarity;
    if (fullSimilarity >= 0.9 && (!bestMatch || fullSimilarity > bestMatch.similarity)) {
      bestMatch = {
        element,
        similarity: fullSimilarity,
        pos,
        node,
        parentNode,
        isSelf,
        isPartial: void 0
      };
    } else if (!bestMatch || bestMatch.isPartial !== void 0) {
      const partialMatchResult = findBestFuzzyMatch(searchContent2, node.content);
      if (!partialMatchResult) continue;
      const partialSimilarity = partialMatchResult.similarity * 0.8;
      if (partialSimilarity >= 0.7 && (!bestMatch || partialSimilarity > bestMatch.similarity)) {
        bestMatch = {
          element,
          similarity: partialSimilarity,
          pos,
          node,
          parentNode,
          isSelf: false,
          isPartial: {
            start: partialMatchResult.start,
            end: partialMatchResult.end,
            similarity: partialMatchResult.similarity
          }
        };
      }
    }
  }
  if (!bestMatch) return null;
  const replaceElement = document.createElement("div");
  replaceElement.innerHTML = diff.replace.value;
  const inCode = bestMatch.node.type.spec.code && !bestMatch.isSelf;
  const replaceContentRaw = inCode ? Fragment.from(editorView.state.schema.text(decodeHtmlEntities(diff.replace.value.replace(/\r\n?/g, "\n")))) : Fragment.from(findRealContent(parser.parse(replaceElement, {
    topNode: editorView.state.schema.nodes.placeholderDoc.create()
  }), diff.replace.value));
  let replaceContent = replaceContentRaw;
  if (!bestMatch.isSelf && !bestMatch.isPartial) {
    replaceContent = bestMatch.node.type.createAndFill(bestMatch.node.attrs, replaceContentRaw)?.content ?? replaceContentRaw;
  } else if (bestMatch.isPartial) {
    replaceContent = Fragment.empty;
    if (bestMatch.isPartial.start > 0) {
      replaceContent = bestMatch.node.content.cut(0, bestMatch.isPartial.start);
    }
    replaceContent = replaceContent.append(replaceContentRaw);
    if (bestMatch.isPartial.end < bestMatch.node.content.size) {
      replaceContent = replaceContent.append(bestMatch.node.content.cut(bestMatch.isPartial.end + 1, bestMatch.node.content.size));
    }
  }
  let forceReplaceSelf = false;
  if (bestMatch.node.type.name === "transcript") {
    let hasTranscriptItems = false;
    replaceContent.forEach((node) => {
      if (node.type.name === "transcriptItem") {
        hasTranscriptItems = true;
        return;
      }
    });
    if (hasTranscriptItems) {
      let newContent = Fragment.empty;
      replaceContent.forEach((node) => {
        if (node.type.name === "transcriptItem") {
          newContent = newContent.append(Fragment.from(node));
        } else if (node.type.name === "paragraph") {
          const transcriptItem = editorView.state.schema.nodes.transcriptItem.createAndFill(node.attrs, node.content);
          if (transcriptItem) newContent = newContent.append(Fragment.from(transcriptItem));
        }
      });
      replaceContent = newContent;
    } else {
      forceReplaceSelf = true;
    }
  }
  if (bestMatch.node.type.name === "transcriptItem" && !bestMatch.isSelf) {
    let newContent = Fragment.empty;
    replaceContent.forEach((node) => {
      if (node.isInline) {
        newContent = newContent.append(Fragment.from(node));
      } else {
        newContent = newContent.append(Fragment.from(editorView.state.schema.text(node.textContent ?? "")));
      }
    });
    replaceContent = newContent;
  } else if (bestMatch.node.type.name === "transcriptItem" && bestMatch.isSelf) {
    let newContent = Fragment.empty;
    replaceContent.forEach((node) => {
      if (node.type.name === "transcriptItem") {
        newContent = newContent.append(Fragment.from(node));
      } else {
        const transcriptItem = editorView.state.schema.nodes.transcriptItem.createAndFill(node.attrs, node.content);
        if (transcriptItem) newContent = newContent.append(Fragment.from(transcriptItem));
      }
    });
    replaceContent = newContent;
  }
  const searchElement = document.createElement("div");
  searchElement.innerHTML = diff.search.value;
  const searchContent = bestMatch.isPartial ? Fragment.from(bestMatch.node.content) : inCode ? Fragment.from(editorView.state.schema.text(decodeHtmlEntities(diff.search.value.replace(/\r\n?/g, "\n")))) : Fragment.from(findRealContent(parser.parse(searchElement, {
    topNode: editorView.state.schema.nodes.placeholderDoc.create()
  }), diff.search.value));
  if (!replaceContent || !(replaceContent instanceof Fragment) || !searchContent || !(searchContent instanceof Fragment)) {
    return null;
  }
  const locator = computeLocator(bestMatch.pos, editorView.state);
  if (!locator) return null;
  const hash = simpleHashString(`${diff.index}-${diff.location}-${diff.search.value}-${diff.replace.value}`);
  return {
    hash,
    locator,
    pos: bestMatch.pos,
    isSelf: bestMatch.isSelf || forceReplaceSelf,
    searchContent,
    replaceContent,
    previewHTML: generateHTMLFromFragment(replaceContent, editorView.state.schema),
    changeDetails: calculateChangeDetails(searchContent, replaceContent),
    context: {
      messageId
    }
  };
};

export { DiffHandler, DiffPluginKey, applyDiffToTransaction, findNodeFromEditorLocator };
//# sourceMappingURL=DiffHandler.js.map
